import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReportTable from "../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter, { DEFAULT_PRESETS } from "../../common/DateRangeFilter";
import ConfirmModal from "../../common/ConfirmModal";
import utils from "../../../utils/utils";
import "./ExternalAssignedStudiesPanel.scss";

const FIELD_OPTIONS = [
  { key: "Study", label: "Study" },
  { key: "Sites", label: "Site" },
  { key: "Category", label: "Category" },
  { key: "Folders", label: "Assigned / total" },
  { key: "Status", label: "Status" },
  { key: "VisibleUntil", label: "Visible until" },
  { key: "AssignedOn", label: "Assigned on" },
  { key: "AssignedBy", label: "Assigned by" },
];

const DEFAULT_COLUMNS = [
  "Study",
  "Sites",
  "Category",
  "Folders",
  "Status",
  "VisibleUntil",
  "AssignedOn",
  "AssignedBy",
];

const STATUS_FILTER_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Expired", label: "Expired" },
];

const EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const COLUMN_DATE_PRESETS = DEFAULT_PRESETS;

const NO_SITES_FILTER = "__none__";

const FILTERS_INITIAL = {
  sites: [],
  categories: [],
  statuses: [],
  assignedBy: [],
  assignedOnRange: { ...EMPTY_DATE_RANGE },
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const matchesDateRange = (value, range) => {
  if (
    !range ||
    range.preset === "All Time" ||
    (!range.startDate && !range.endDate)
  ) {
    return true;
  }
  const raw = String(value ?? "").trim();
  if (!raw || /^unlimited$/i.test(raw)) return false;
  const date = utils.parseDateInput(raw);
  if (!date) return false;
  const start = range.startDate
    ? startOfDay(utils.parseDateInput(range.startDate))
    : null;
  const end = range.endDate
    ? endOfDay(utils.parseDateInput(range.endDate))
    : null;
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
};

const rowSiteNames = (row) => {
  if (Array.isArray(row?.SiteNames) && row.SiteNames.length) {
    return row.SiteNames.map((name) => String(name || "").trim()).filter(Boolean);
  }
  const text = String(row?.Sites || "").trim();
  if (!text || /^no sites$/i.test(text) || text === "—") return [];
  return text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

const rowCategories = (row) => {
  if (Array.isArray(row?.Categories) && row.Categories.length) {
    return row.Categories.map((name) => String(name || "").trim()).filter(Boolean);
  }
  return String(row?.Category || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

const applyAssignedStudyFilters = (rows, filters) =>
  (rows || []).filter((row) => {
    const selectedSites = filters.sites || [];
    if (selectedSites.length) {
      const names = rowSiteNames(row);
      const ids = (row?.SiteIds || []).map((id) => String(id || "").trim());
      const tokens = new Set([...names, ...ids]);
      const matches = selectedSites.some((value) => {
        if (value === NO_SITES_FILTER) return names.length === 0;
        return tokens.has(value);
      });
      if (!matches) return false;
    }

    const selectedCategories = filters.categories || [];
    if (selectedCategories.length) {
      const cats = rowCategories(row);
      if (!selectedCategories.some((value) => cats.includes(value))) {
        return false;
      }
    }

    const selectedStatuses = filters.statuses || [];
    if (selectedStatuses.length && !selectedStatuses.includes(row?.Status)) {
      return false;
    }

    const selectedAssignedBy = filters.assignedBy || [];
    if (
      selectedAssignedBy.length &&
      !selectedAssignedBy.includes(String(row?.AssignedBy || "").trim())
    ) {
      return false;
    }

    return matchesDateRange(row?.AssignedOnRaw || row?.AssignedOn, filters.assignedOnRange);
  });

function ExternalAssignedStudiesPanel({
  staffId,
  canEdit = false,
  assignedRows = [],
  loading = false,
  renderAssignStep,
  onBeginAssign,
  onCancelAssign,
  onCommitAssign,
  onSaveAssign,
  onRemoveStudy,
  /** When true, hide the built-in Assign study toolbar (parent renders its own). */
  hideAssignToolbar = false,
  /** Increment to open the Assign study modal in add mode (for external toolbar). */
  assignRequestNonce = 0,
  /** Active users available for multi-assign (add mode). */
  assignUserOptions = null,
  /** Controlled staff ids selected in the assign modal (add mode). */
  assignSelectedStaffIds = null,
  onAssignSelectedStaffIdsChange = null,
  /** Returns a validation message when study/site selection is incomplete. */
  getAssignShareError = null,
}) {
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("add");
  const [saving, setSaving] = useState(false);
  const [tableFilters, setTableFilters] = useState(FILTERS_INITIAL);
  const [removeConfirm, setRemoveConfirm] = useState({
    open: false,
    row: null,
  });
  const lastAssignRequestNonceRef = useRef(null);

  const showUserPicker =
    assignMode === "add" &&
    Array.isArray(assignUserOptions) &&
    typeof onAssignSelectedStaffIdsChange === "function";

  const selectedAssignIds = useMemo(() => {
    if (!Array.isArray(assignSelectedStaffIds)) return [];
    return assignSelectedStaffIds.map((id) => String(id || "").trim()).filter(Boolean);
  }, [assignSelectedStaffIds]);

  // Recomputed every render so study/site picks from the parent scoping hook refresh readiness.
  const assignShareError =
    typeof getAssignShareError === "function"
      ? String(getAssignShareError() || "").trim()
      : "";
  const usersReady = !showUserPicker || selectedAssignIds.length > 0;
  const assignReady = usersReady && !assignShareError;
  const assignDisabledReason = !usersReady
    ? "Select at least one user."
    : assignShareError || "";

  const toggleAssignUser = (staffIdValue, checked) => {
    const id = String(staffIdValue || "").trim();
    if (!id || !onAssignSelectedStaffIdsChange) return;
    const current = new Set(selectedAssignIds.map((x) => x.toLowerCase()));
    const next = [];
    if (checked) {
      selectedAssignIds.forEach((existing) => next.push(existing));
      if (![...current].includes(id.toLowerCase())) next.push(id);
    } else {
      selectedAssignIds.forEach((existing) => {
        if (existing.toLowerCase() !== id.toLowerCase()) next.push(existing);
      });
    }
    onAssignSelectedStaffIdsChange(next);
  };

  const toggleAssignSelectAll = () => {
    if (!onAssignSelectedStaffIdsChange) return;
    const allIds = (assignUserOptions || [])
      .map((row) => String(row?.StaffId || "").trim())
      .filter(Boolean);
    const allSelected =
      allIds.length > 0 &&
      allIds.every((id) =>
        selectedAssignIds.some((x) => x.toLowerCase() === id.toLowerCase()),
      );
    onAssignSelectedStaffIdsChange(allSelected ? [] : allIds);
  };

  const sourceRows = useMemo(() => assignedRows || [], [assignedRows]);

  const siteFilterOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    let hasEmpty = false;
    sourceRows.forEach((row) => {
      const names = rowSiteNames(row);
      const ids = (row?.SiteIds || []).map((id) => String(id || "").trim());
      if (!names.length && !ids.length) hasEmpty = true;
      names.forEach((name, index) => {
        const value = ids[index] || name;
        const key = String(value).toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        options.push({ value, label: name });
      });
    });
    options.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
    if (hasEmpty) {
      options.push({ value: NO_SITES_FILTER, label: "No sites" });
    }
    return options;
  }, [sourceRows]);

  const assignedByFilterOptions = useMemo(() => {
    const seen = new Set();
    sourceRows.forEach((row) => {
      const name = String(row?.AssignedBy || "").trim();
      if (!name || name === "—") return;
      seen.add(name);
    });
    return [...seen]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((value) => ({ value, label: value }));
  }, [sourceRows]);

  const categoryFilterOptions = useMemo(() => {
    const seen = new Set();
    sourceRows.forEach((row) => {
      rowCategories(row).forEach((name) => seen.add(name));
    });
    const values = [...seen].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
    if (!values.length) return [{ value: "eISF", label: "eISF" }];
    return values.map((value) => ({ value, label: value }));
  }, [sourceRows]);

  const tableRows = useMemo(
    () => applyAssignedStudyFilters(sourceRows, tableFilters),
    [sourceRows, tableFilters],
  );

  const updateTableFilters = useCallback((patch) => {
    setTableFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    if (
      field === "Sites" ||
      field === "Category" ||
      field === "Status" ||
      field === "AssignedOn" ||
      field === "AssignedBy"
    ) {
      return true;
    }
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;
    if (field === "Study") {
      return [row.Study, row.StudyName, row.StudyNumber]
        .map((value) => String(value ?? "").toLowerCase())
        .some((value) => value.includes(term));
    }
    return String(row[field] ?? "")
      .toLowerCase()
      .includes(term);
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "Sites") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={siteFilterOptions}
            value={tableFilters.sites}
            onChange={(values) => updateTableFilters({ sites: values })}
            emptyLabel="All"
            addLabel="+"
            popoverTitle="Filter by site"
            popoverSubtitle="Select one or more sites"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "Category") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search external-assigned-studies__column-select"
            value={tableFilters.categories[0] || ""}
            disabled={options?.disabled}
            onChange={(event) =>
              updateTableFilters({
                categories: event.target.value ? [event.target.value] : [],
              })
            }
          >
            <option value="">All</option>
            {categoryFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "Status") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search external-assigned-studies__column-select"
            value={tableFilters.statuses[0] || ""}
            disabled={options?.disabled}
            onChange={(event) =>
              updateTableFilters({
                statuses: event.target.value ? [event.target.value] : [],
              })
            }
          >
            <option value="">All</option>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "AssignedOn") {
        if (options?.checkOnly) return true;
        return (
          <DateRangeFilter
            value={tableFilters.assignedOnRange}
            onChange={(range) =>
              updateTableFilters({
                assignedOnRange: range || { ...EMPTY_DATE_RANGE },
              })
            }
            placeholder="Date range"
            presetOptions={COLUMN_DATE_PRESETS}
            compact
            disabled={options?.disabled}
          />
        );
      }

      if (field === "AssignedBy") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={assignedByFilterOptions}
            value={tableFilters.assignedBy}
            onChange={(values) => updateTableFilters({ assignedBy: values })}
            emptyLabel="All"
            addLabel="+"
            popoverTitle="Filter by assigned by"
            popoverSubtitle="Select one or more people"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      return null;
    },
    [
      assignedByFilterOptions,
      categoryFilterOptions,
      siteFilterOptions,
      tableFilters,
      updateTableFilters,
    ],
  );

  const openAssign = (mode, rowOrStudyId) => {
    if (!canEdit && mode === "edit") return;
    const row =
      rowOrStudyId && typeof rowOrStudyId === "object" ? rowOrStudyId : null;
    const studyId = row?.StudyId || rowOrStudyId || "";
    setAssignMode(mode);
    if (
      mode === "add" &&
      typeof onAssignSelectedStaffIdsChange === "function"
    ) {
      const focusId = String(staffId || "").trim();
      const focusActive =
        focusId &&
        (assignUserOptions || []).some(
          (opt) =>
            String(opt?.StaffId || "").toLowerCase() === focusId.toLowerCase(),
        );
      onAssignSelectedStaffIdsChange(focusActive ? [focusId] : []);
    }
    onBeginAssign?.(
      studyId,
      row?.SiteId ? { siteId: row.SiteId, category: row.Category } : undefined,
    );
    setAssignOpen(true);
  };

  useEffect(() => {
    if (!canEdit) return;
    // On first mount (e.g. tab switch remount), sync nonce without opening.
    if (lastAssignRequestNonceRef.current === null) {
      lastAssignRequestNonceRef.current = assignRequestNonce;
      return;
    }
    if (!assignRequestNonce) return;
    if (assignRequestNonce === lastAssignRequestNonceRef.current) return;
    lastAssignRequestNonceRef.current = assignRequestNonce;
    openAssign("add");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open when parent requests
  }, [assignRequestNonce, canEdit]);

  const renderCell = (row, field) => {
    if (field === "Study") {
      const studyName = row.StudyName || row.Study || "—";
      const studyNumber = row.StudyNumber || "—";
      const color = row.StudyColor || "";
      const nameNode = canEdit ? (
        <button
          type="button"
          className="report-table__cell-link study-mgmt-report__study-link"
          onClick={() => openAssign("edit", row)}
          title={studyName}
        >
          <span className="study-mgmt-report__study-link-label">{studyName}</span>
        </button>
      ) : (
        <span className="study-mgmt-report__study-link-label">{studyName}</span>
      );
      return (
        <span className="study-mgmt-report__study-cell">
          {nameNode}
          <span
            className="study-mgmt-report__number-badge"
            style={color ? { "--study-color": color } : undefined}
            title={studyNumber}
          >
            <span className="study-mgmt-report__color-dot" aria-hidden="true" />
            <span className="study-mgmt-report__number-badge-text">
              {studyNumber}
            </span>
          </span>
        </span>
      );
    }
    if (field === "Status") {
      const tone = String(row.Status || "").toLowerCase();
      return (
        <span className="external-assigned-studies__until">
          <span
            className={`external-assigned-studies__status is-${tone || "active"}`}
          >
            {row.Status || "Active"}
          </span>
          {canEdit ? (
            <button
              type="button"
              className="external-assigned-studies__remove"
              title="Remove study sharing"
              aria-label={`Remove ${row.Study || "study"}`}
              onClick={() => setRemoveConfirm({ open: true, row })}
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
            </button>
          ) : null}
        </span>
      );
    }
    if (field === "VisibleUntil") {
      return row.VisibleUntil || "Unlimited";
    }
    if (field === "AssignedBy") {
      return row.AssignedBy || "—";
    }
    if (field === "AssignedOn") {
      return row.AssignedOn || "—";
    }
    return row[field] ?? "—";
  };

  const closeAssign = (restore) => {
    if (restore) onCancelAssign?.();
    else onCommitAssign?.();
    setAssignOpen(false);
  };

  const handleSaveAssign = async () => {
    if (
      showUserPicker &&
      selectedAssignIds.length === 0
    ) {
      // Parent save handler also validates; keep a fast UI guard.
      return;
    }
    setSaving(true);
    try {
      const ok = await onSaveAssign?.();
      if (ok) closeAssign(false);
    } finally {
      setSaving(false);
    }
  };

  const modal =
    assignOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            className="modal fade show d-block external-assign-study-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="external-assign-study-title"
          >
            <div className="modal-dialog external-assign-study-modal__dialog">
              <div className="modal-content external-assign-study-modal__content">
                <div className="modal-header external-assign-study-modal__header">
                  <h5 className="modal-title" id="external-assign-study-title">
                    {assignMode === "edit"
                      ? "Edit study sharing"
                      : "Assign study"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => closeAssign(true)}
                  />
                </div>
                <div className="modal-body external-assign-study-modal__body">
                  {renderAssignStep?.()}
                  {showUserPicker ? (
                    <div className="external-assign-study-modal__users">
                      <div className="external-assign-study-modal__users-head">
                        <span className="external-assign-study-modal__users-title">
                          Assign to users
                          <span className="mandatory"> *</span>
                        </span>
                        {(assignUserOptions || []).length > 0 ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm external-assign-study-modal__users-select-all"
                            onClick={toggleAssignSelectAll}
                          >
                            {selectedAssignIds.length > 0 &&
                            (assignUserOptions || []).every((opt) =>
                              selectedAssignIds.some(
                                (id) =>
                                  String(id).toLowerCase() ===
                                  String(opt?.StaffId || "").toLowerCase(),
                              ),
                            )
                              ? "Clear all"
                              : "Select all"}
                          </button>
                        ) : null}
                      </div>
                      <div className="external-assign-study-modal__users-list">
                        {(assignUserOptions || []).length === 0 ? (
                          <div className="external-assign-study-modal__users-empty">
                            No active Sponsor/CRO users available.
                          </div>
                        ) : (
                          (assignUserOptions || []).map((opt) => {
                            const id = String(opt?.StaffId || "");
                            const name =
                              String(opt?.Name || "").trim() ||
                              [
                                opt?.Forename || opt?.FirstName,
                                opt?.Surname || opt?.LastName,
                              ]
                                .map((v) => String(v || "").trim())
                                .filter(Boolean)
                                .join(" ") ||
                              "—";
                            const emp = String(opt?.EmployeeNum || "").trim();
                            const checked = selectedAssignIds.some(
                              (x) =>
                                x.toLowerCase() === id.toLowerCase(),
                            );
                            return (
                              <label
                                key={id}
                                className={`external-assign-study-modal__user-option${
                                  checked ? " is-checked" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    toggleAssignUser(id, e.target.checked)
                                  }
                                />
                                <span className="external-assign-study-modal__user-option-text">
                                  <span className="external-assign-study-modal__user-option-name">
                                    {name}
                                  </span>
                                  {emp ? (
                                    <span className="external-assign-study-modal__user-option-emp">
                                      {emp}
                                    </span>
                                  ) : null}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      {selectedAssignIds.length > 1 ? (
                        <p className="external-assign-study-modal__users-hint">
                          This assignment will apply to {selectedAssignIds.length}{" "}
                          users.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => closeAssign(true)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  {canEdit ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveAssign}
                      disabled={saving || !assignReady}
                      title={
                        saving || assignReady
                          ? undefined
                          : assignDisabledReason ||
                            "Complete all mandatory fields to continue."
                      }
                    >
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      {saving
                        ? "Saving…"
                        : assignMode === "edit"
                          ? "Update"
                          : "Assign"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="external-assigned-studies">
      {canEdit && !hideAssignToolbar ? (
        <div className="external-assigned-studies__header">
          <div className="external-assigned-studies__toolbar">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => openAssign("add")}
            >
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Assign study
            </button>
          </div>
        </div>
      ) : null}

      <div className="external-assigned-studies__report-wrap">
        <ReportTable
          rows={tableRows}
          fieldOptions={FIELD_OPTIONS}
          defaultColumns={DEFAULT_COLUMNS}
          pageSizeOptions={[10, 25, 50]}
          columnSearchMode="client"
          persistLayoutKey={`external-assigned-studies-v7-${staffId || "profile"}`}
          loading={loading}
          emptyMessage="No studies shared yet"
          emptyDescription={
            canEdit
              ? "Use Assign study to share a study, site and folders."
              : "No studies have been shared with this user."
          }
          renderCell={renderCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          hideColumnHeaderActionsFields={[
            "Sites",
            "Category",
            "Status",
            "AssignedOn",
            "AssignedBy",
          ]}
          getRowKey={(row) => row.id}
        />
      </div>

      <ConfirmModal
        isOpen={removeConfirm.open}
        onClose={() => setRemoveConfirm({ open: false, row: null })}
        title="Remove study sharing?"
        message={
          removeConfirm.row
            ? `Remove sharing for ${removeConfirm.row.Study}${
                removeConfirm.row.Sites &&
                removeConfirm.row.Sites !== "No sites"
                  ? ` at ${removeConfirm.row.Sites}`
                  : ""
              }${
                removeConfirm.row.Category
                  ? ` (${removeConfirm.row.Category})`
                  : ""
              }? This user will lose that site and category access.`
            : "Remove this study sharing?"
        }
        cancelText="Cancel"
        confirmText="Remove"
        confirmButtonClass="btn-danger"
        icon="fas fa-times-circle text-danger"
        onConfirm={async () => {
          const row = removeConfirm.row;
          setRemoveConfirm({ open: false, row: null });
          if (row?.StudyId) await onRemoveStudy?.(row);
        }}
      />

      {modal}
    </div>
  );
}

export default ExternalAssignedStudiesPanel;
